import React, { useEffect } from "react";
import { useStore } from "zustand";
import { useAppStateStore, useLeadStore } from "../../../../stores";
import { InputText } from "../index";
import validationProps from "./validationProps";
import {
  errorService,
  leadsService,
  zipCodeService,
} from "../../../../services";
import { zipCodeRegex } from "../../../../utilities";

const handleOnAddressChange = (e) => {
  useLeadStore.set({
    consumer: {
      ...useLeadStore.get("consumer"),
      address1: e.target.value,
    },
  });
};

const handleOnCityChange = (e) => {
  useLeadStore.set({
    consumer: {
      ...useLeadStore.get("consumer"),
      city: e.target.value,
    },
  });
};

const validateAddress = () => {
  const { address1 } = validationProps("address1");
  useAppStateStore.setValidity("aboutYou", { address: !!address1 });
};

const validateCity = () => {
  const { city } = validationProps("city");
  useAppStateStore.setValidity("aboutYou", { city: !!city });
};

const InputAddress = () => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);

  useEffect(() => {
    validateAddress();
  }, [lead.consumer.address1]);

  return (
    <InputText
      id="address"
      dataTestId="address"
      label="Address"
      errorText="Please enter your address"
      onChange={handleOnAddressChange}
      value={lead.consumer.address1}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.address}
      className="col-12 pb-2"
      displayRequirementIndicator={true}
    />
  );
};

const InputCity = () => {
  const appState = useStore(useAppStateStore);
  const lead = useStore(useLeadStore);

  useEffect(() => {
    validateCity();
  }, [lead.consumer.city]);

  return (
    <InputText
      id="city"
      dataTestId="city"
      label="City"
      errorText="Please enter your city"
      onChange={handleOnCityChange}
      value={lead.consumer.city}
      hasError={appState.formSubmitted && !appState.isValid.aboutYou.city}
      displayRequirementIndicator={true}
    />
  );
};

const InputZipCode = ({ inputFocus, valueEntered, className }) => {
  const lead = useStore(useLeadStore);
  const appState = useStore(useAppStateStore);

  const lookupZip = async (zip) => {
    try {
      const response = await zipCodeService.validate(zip);
      const responseInvalid =
        (response.hasOwnProperty("status") && response.status === 404) ||
        !response.city ||
        !response.state;
      if (responseInvalid) return false;
      leadsService.set({
        consumer: {
          ...lead.consumer,
          zip,
          city: response.city,
          state: response.state,
          cityForLite: response.city,
        },
      });
      return response;
    } catch (error) {
      if (error.status === 404) return false;
      errorService.handle(error, lead, errorService.ERROR_TYPES.recoverable);
    }
  };

  const validateZip = async (zip) => {
    const zipRegexLooksGood = zipCodeRegex.test(zip);
    const zipIsKnown = zipRegexLooksGood ? await lookupZip(zip) : false;
    useAppStateStore.setValidity("describeYourVehicle", {
      zip: !!zipRegexLooksGood && !!zipIsKnown,
    });
  };

  const handleOnChangeZip = (event) => {
    const zip = event?.target?.value?.replace(/[^\d.-]+/g, "");
    leadsService.set({
      consumer: { ...lead.consumer, zip },
    });
    (async () => await validateZip(zip))();
  };

  useEffect(() => {
    (async () => await validateZip(lead.consumer.zip))();
  }, []);

  return (
    <InputText
      id="zip"
      label="ZIP Code"
      inputMode="numeric"
      minLength="5"
      maxLength="5"
      value={lead.consumer.zip}
      onChange={handleOnChangeZip}
      onBlur={(event) => {
        valueEntered(event, "zip");
      }}
      onFocus={() => inputFocus("zip")}
      errorText="Please enter a valid ZIP Code"
      hasError={
        appState.formSubmitted && !appState.isValid.describeYourVehicle.zip
      }
      className={className}
      displayRequirementIndicator={true}
    />
  );
};

const InfoStreetAddress = () => {
  const lead = useStore(useLeadStore);
  return (
    <>
      <div className="row">
        <InputAddress />
      </div>
      <div className="row d-flex align-items-center">
        <InputCity />
        <div className="col-sm-6 pt-2 mt-sm-3 fs-14p ps-4 pb-2 h-50">
          {`${lead.consumer.state}, ${lead.consumer.zip}`}
        </div>
      </div>
    </>
  );
};

export default InfoStreetAddress;
export { InputAddress, InputCity, InputZipCode };
